export interface IHorseVariant {
  id?: number;
  horse_variant_id?: number;
  image_src: string;
  title?: string;
  product_title: string;
  variant_title: string;
  sku: string;
  vendor: string;
  product_type: string;
  tracked?: boolean;
  currently_available?: number;
  committed?: number;
  incoming?: number;
  pack_size?: number;
  ordered?: number;
  sales_quantity?: number;
  available_days?: number;
  estimated_days_of_sales_remaining?: number;
  sales_rate?: number;
  cost?: number;
  price?: number;
  discarded?: boolean;
  discarded_at?: Date;
  recommendations_enabled: boolean;
  weight?: number;
  height?: number;
  length?: number;
  width?: number;
  barcode?: string;
  warehouse_location?: string;
  tags?: string[];
  min?: number;
}

export interface Comment {
  id: number;
  user: string;
  content: string;
  created_at: string;
}

export interface PostComment {
  content: string;
}

export interface SalesTrendsTotalsAndMax {
  total_sales_rate: number;
  total_sales: number;
  max_sales_rate: number;
  total_ordered: number;
  total_available: number;
  total_revenue: number;
}

export interface Option {
  label: string;
  value: string;
  key: string;
}

// The names a user can edit in Horse. committed and incoming are managed
// (orders; purchase and transfer orders) and on_hand is generated.
export const EDITABLE_INVENTORY_NAMES = [
  "available",
  "reserved",
  "damaged",
  "safety_stock",
  "quality_control",
] as const;
export type EditableInventoryName = (typeof EDITABLE_INVENTORY_NAMES)[number];

export interface IHorseInventoryLevel {
  id?: number;
  horse_location_id: number;
  horse_variant_id: number;
  available: number;
  incoming: number;
  committed: number;
  reserved: number;
  damaged: number;
  safety_stock: number;
  quality_control: number;
  on_hand: number;
  horse_location: {
    id: number;
    name: string;
  };
}

export interface IUpdateOrCreateHorseInventoryLevel {
  id?: number;
  horse_location_id: number;
  horse_variant_id: number;
  available?: number;
  reserved?: number;
  damaged?: number;
  safety_stock?: number;
  quality_control?: number;
  reason?: string;
  comment?: string;
}

export interface HorseInventoryLevelUpdatePayload {
  horse_variant_id: number;
  horse_inventory_levels: IUpdateOrCreateHorseInventoryLevel[];
  tracked: boolean;
  reason: string;
  comment: string;
}

export interface Supplier {
  id: number;
  name: string;
  email: string;
  delivery_lead_days: number;
  user_id: number;
  discarded_at: string;
  discarded: boolean;
  currency: string;
  notes: string;
  minimum_order_value: number;
}

export interface Vendor {
  id: number;
  name: string;
  email: string;
  delivery_lead_days: number;
  user_id: number;
  currency: string;
  notes: string;
  minimum_order_value: number;
}

export interface PurchaseOrderVendorSettings {
  id: number;
  name: string;
  email: string | null;
  minimum_order_value: number | null;
}

export interface ISupplierSearchOption {
  name: string;
  id: number;
}

export interface PaginatedResponse<T> {
  rows: T[];
  hasNext: boolean;
  hasPrev: boolean;
}

export interface FormattedShopifyChannel {
  helpText: string;
  label: string;
  type: "available" | "unavailable";
  value: string;
  shopifyChannelId: number;
}

export interface SupplierHorseVariant {
  id: number;
  supplier_id: number;
  horse_variant_id: number;
  minimum_order_quantity: number;
  pack_size: number;
  supplier_sku: string;
  cost: number;
  product_title: string;
  tracked: boolean;
  variant_title: string;
  sku: string;
  product_type: string;
  vendor: string;
  image_src: string;
  discarded: boolean;
}

export interface VendorHorseVariantRow {
  id: number | null;
  vendor_horse_variant_id: number | null;
  vendor_id: number | null;
  horse_variant_id: number;
  minimum_order_quantity: number | null;
  pack_size: number | null;
  vendor_sku: string | null;
  cost: number | null;
  product_title: string;
  tracked: boolean;
  variant_title: string;
  sku: string;
  barcode: string;
  product_type: string;
  vendor: string;
  horse_variant_cost: number | null;
  image_src: string;
  discarded: boolean;
  created_at: string;
}

export type ChildBundleHorseVariant = IHorseVariant & {
  bundle_id: number;
  quantity: number;
  discarded: boolean;
};

export interface Bundle {
  id: number;
  name: string;
  user_id: number;
  horse_variant_id: number;
  horseVariant?: IHorseVariant;
  parent_inventory_tracks_children: boolean;
}

export enum PurchaseOrderState {
  Draft = "draft",
  Ordered = "ordered",
  Closed = "closed",
}

export interface IPurchaseOrder {
  id: number;
  vendor?: string;
  horse_location_id: number;
  destination: string;
  arrival_date?: Date;
  forecasting_method?: "sales_rate" | "seasonal";
  sampling_period?: number;
  days_of_inventory?: number;
  unbuyable_choice?: string;
  buffer_type?: string;
  buffer_value?: number;
  growth_factor?: number;
  state: PurchaseOrderState;
  created_at: Date;
  updated_at: Date;
  currency: string;
  cargo_control_number?: string;
  entry_summary_id?: string;
  exchange_rate?: number;
  release_date?: string;
  label?: string;
  note?: string;
  supplier_id?: number;
  supplier?: Supplier;
  vendorSettings?: PurchaseOrderVendorSettings;
  amount_paid: string | null;
  payment_due_date: string | null;
}

export type PaymentStatus = "Unpaid" | "Partially paid" | "Paid";
export type PaymentDueDateStatus = "Overdue" | "Not overdue";
export type PurchaseOrderPaymentAttributes = Partial<Pick<IPurchaseOrder, "amount_paid" | "payment_due_date">>;

export type CreatePurchaseOrder =
  | {
      horse_location_id: number;
      supplier_id?: never;
      vendor: string;
    }
  | {
      horse_location_id: number;
      supplier_id: number;
      vendor?: never;
    };

export interface PurchaseOrderUpdatePayload
  extends Omit<Partial<IPurchaseOrder>, "id" | "updated_at" | "created_at" | "destination" | "supplier" | "vendorSettings"> {
  id?: never;
  updated_at?: never;
  created_at?: never;
  destination?: never;
  supplier?: never;
  vendorSettings?: never;
  sync_costs_requested?: boolean;
  purchase_order_line_items_attributes?: PurchaseOrderLineItemUpdatePayload[];
}

export interface PurchaseOrderLineItemUpdatePayload {
  id: number;
  cost?: number | string;
  price?: number | string;
  description_of_goods?: string;
  entry_summary_line_number?: string;
  classification_number?: string;
  duty_rate?: number | string;
  horse_variant_id?: number;
  quantity?: number | string;
  received?: number | string;
  rejected?: number | string;
}

export interface IPurchaseOrderLineItem {
  id: number;
  variant_title: string;
  sku: string;
  barcode: string;
  horse_variant_id: number;
  product_title: string;
  vendor: string;
  product_type: string;
  received: number;
  rejected: number;
  quantity: number;
  cost: number;
  price: number;
  description_of_goods: string;
  entry_summary_line_number: number;
  classification_number: string;
  duty_rate: number;
  image_src: string;
  tracked: boolean;
  currently_available: number;
  ordered_quantity?: number;
  needed: number;
  oos_date: Date;
  line_total: number;
  sales_rate: number;
  sales_quantity: number;
  available_days: number;
  created_at: Date;
  shopify_image_id: string;
  warehouse_location: string;
  supplier_cost?: number;
  minimum_order_quantity?: number;
  pack_size?: number;
  supplier_sku?: string;
  relevant_purchase_order_quantity?: number;
  discarded?: boolean;
  min?: number;
}

// Overwrite cost, quantity as strings instead of numbers
export type EditablePurchaseOrderLineItem = Omit<IPurchaseOrderLineItem, "cost" | "quantity"> & {
  cost: string;
  quantity: string;
  sortIndex: number;
  recommended_quantity: number;
};

export type PurchaseOrderLineItems = Record<number, EditablePurchaseOrderLineItem>;

export interface RangeFilter<T> {
  more_than?: T;
  less_than?: T;
}

export type NumberRangeFilter = RangeFilter<number>;

// Needed to support the user typing a decimal
export type DecimalRangeFilter = RangeFilter<string>;

export interface DateRangeFilter {
  start?: Date;
  end?: Date;
}

export interface CalendarDateRangeFilter {
  start?: string;
  end?: string;
}

export interface SortFilter {
  column: string;
  direction: string;
}

export type BaseQueryParams = Record<
  string,
  | Date
  | DateRangeFilter
  | CalendarDateRangeFilter
  | DecimalRangeFilter
  | number[]
  | NumberRangeFilter
  | SortFilter
  | string[]
  | boolean
  | number
  | string
  | undefined
  | null
>;

export type QueryParams<T extends BaseQueryParams = BaseQueryParams> = T;

export interface HomeRecommendationResponse {
  variant_title: string;
  sku: string;
  product_title: string;
  vendor: string;
  product_type: string;
  horse_variant_id: number;
  image_src: string;
  oos_date?: Date;
  available?: number;
  amount: number;
  min?: number;
}

export interface HomeRecommendationsResponse {
  oos_lost: number;
  ooss_future_lost: number;
  os_frozen: number;

  out_of_stock_soon: HomeRecommendationResponse[];
  out_of_stock: HomeRecommendationResponse[];
  over_stock: HomeRecommendationResponse[];

  primary_location_id: number;

  below_min: HomeRecommendationResponse[];
}

export interface ShopifyVariant {
  id: number;
  title: string;
  image_src: string;
  shop_domain: string;
  variant_url: string;
  price: number;
  discarded: boolean;
}

export interface HorseLocation {
  id: number;
  name: string;
  user_id: number;
  discarded_at?: Date;
  discarded?: boolean;
  country_code?: string;
  country?: string;
  city?: string;
  province?: string;
  province_code?: string;
  zip?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  localized_country_name?: string;
  localized_province_name?: string;
  legacy?: boolean;
  shopify_updated_at?: Date;
  shopify_locations_count?: number;
  created_at: Date;
  updated_at: Date;
}

export interface LinkedLocation {
  id: number;
  shopify_location_id: number;
  name: string;
  shopifyDomain: string;
}

export interface TransferOrder {
  id: number;
  origin_horse_location_id: number;
  destination_horse_location_id: number;
  state: string;
  label?: string;
  estimated_arrival_date?: Date;
  sent_date?: Date;
  arrived_date?: Date;
  created_at: Date;
  updated_at: Date;
}

export interface IndexTransferOrder {
  id: number;
  label: string;
  origin?: HorseLocation;
  destination?: HorseLocation;
  // Renamed from state to status
  status: "Draft" | "Sent" | "Closed";
  sent: number;
  received: number;
  quantity: number;
  value: number;
}

export interface TransferOrderLineItem {
  id: number;
  variant_title: string;
  sku: string;
  barcode: string;
  product_title: string;
  vendor: string;
  horse_variant_id: number;
  product_type: string;
  quantity_received: number;
  quantity_sent: number;
  image_src: string;
  warehouse_location: string;
  cost: number;
  currently_available: number;
  tracked: boolean;
}

export type ArchivedChoice = "all" | "unarchived";

export interface SalesTrendsQueryParams extends BaseQueryParams {
  sampling_period?: string;
  info_query?: "never_available" |  "no_sales" |  "out_of_stock" |  "out_of_stock_quite_soon" |  "out_of_stock_soon_ish" |  "overstock" |  "below_min";
  forecasting_method?: string;
  horse_location_id?: string;
  page?: number;
  per_page?: number;
  product_type?: string;
  search?: string;
  unarchived_horse_variants?: ArchivedChoice;
  vendor?: string;
  supplier_id?: string;
  published_shopify_channel_ids?: number[];
  unpublished_shopify_channel_ids?: number[];
  sale_shopify_channel_ids?: string[];
  product_tags?: string[];
  by_cost?: NumberRangeFilter;
  by_currently_available?: NumberRangeFilter;
  by_ordered_quantity?: NumberRangeFilter;
  by_sales_quantity?: NumberRangeFilter;
  by_sales_rate?: NumberRangeFilter;
  by_weight?: NumberRangeFilter;
  by_available_days?: NumberRangeFilter;
  by_sort?: SortFilter;
}

export interface CostAdjustment {
  id?: number;
  label: string;
  amount: number;
  purchase_order_id: number;
  _destroy?: boolean;
}

export interface CostAdjustmentEditable {
  id?: number;
  label: string;
  // String while it's being edited client-side
  amount: string;
  purchase_order_id: number;
  _destroy?: boolean;
}

export interface IShopifyShop {
  shopify_domain: string;
  name: string;
  email: string;
  import_step?: string;
  readyForRecommendations?: boolean;
}

export class ShopifyShop implements IShopifyShop {
  shopify_domain: string;
  name: string;
  email: string;
  import_step?: string;
  readyForRecommendations?: boolean;

  constructor(attributes: IShopifyShop) {
    Object.assign(this, attributes);
  }
}

export interface Adjustable {
  adjustment_type: string;
  url: string;
  purchase_order?: {
    label: string;
  };
  transfer_order?: {
    label: string;
  };
}

export interface InventoryAdjustment {
  id: number;
  adjustment_tye: string;
  url: string;
  adjustable: Adjustable;
}

export interface InventoryLevelHistory {
  id: number;
  created_at: Date;
  horse_location_name: string;
  horse_location_id: number;
  horse_variant_id: number;
  horse_variant_image_src: string;
  horse_variant_discarded: boolean;
  horse_variant_title: string;
  horse_variant_variant_title: string;
  horse_variant_sku: string;
  horse_variant_product_type: string;
  horse_variant_vendor: string;
  adjustment_type: string;
  quantity_change: number;
  inventory_name: string;
  new_quantity: number;
  missing_inventory_adjustment_reason: string;
  url: string;
  adjustable_id: number;
  adjustable_type: string;
  inventory_adjustment?: InventoryAdjustment;
  // Present on fulfillment_order rows; terminal statuses pin down the history
  fulfillment_order_status?: string | null;
  reason: string;
  comment: string;
}

export interface IndexPurchaseOrder {
  id: number;
  vendor: string;
  supplierName: string;
  horse_location_name: string;
  status: "Draft" | "Ordered" | "Closed";
  ordered_date?: Date;
  date_received?: Date;
  cost: number;
  label: string;
  quantity: number;
  received: number;
  currency: string;
  amount_paid: string | null;
  payment_due_date: string | null;
  payment_status: PaymentStatus | null;
  payment_due_date_status: PaymentDueDateStatus | null;
}

export interface User {
  id: number;
  currency: string;
  syncLevel: string;
  syncbackSetting: string;
  costsyncSetting: string;
  companyName: string;
  companyAddress: string;
  brandLogo: string;
  email: string;
  includeCostAdjustment: boolean;
  recommendation_subscription: boolean;
}
export interface UserResponse {
  user: User;
}
export interface UpdateUser {
  currency: string;
  company_name: string;
  company_address: string;
  sync_level: string;
  syncback_setting: string;
  costsync_setting: string;
  brand_logo: string;
  email?: string;
  include_cost_adjustment: boolean;
  recommendation_subscription: boolean;
}
export interface Banner {
  info?: string;
  progress?: number;
  estimate_time_remaining?: number;
}

export interface PurchaseOrderLineItemsQueryParams extends BaseQueryParams {
  by_sort?: string;
  justify?: boolean;
  published_shopify_channel_ids?: number[];
  unpublished_shopify_channel_ids?: number[];
  sale_shopify_channel_ids?: number[];
  arrival_date?: Date;
  days_of_inventory?: number;
}

export interface HomeQueryParams extends BaseQueryParams {
  horse_location_id: number[];
  sale_shopify_channel_ids: number[];
}

export interface RecommendationQueryParams extends QueryParams {
  minimum_needed?: number;
  product_type?: string;
  sale_shopify_channel_ids?: number[];
  published_shopify_channel_ids?: number[];
  unpublished_shopify_channel_ids?: number[];
  sampling_period?: number;
  forecasting_method?: string;
  arrival_date?: Date;
  days_of_inventory?: number;
  include_all_out_of_stock_variants?: boolean;
}

// From node_modules/@shopify/polaris/build/ts/src/components/ChoiceList/ChoiceList.d.ts
export interface Choice {
  /** Value of the choice */
  value: string;
  /** Label for the choice */
  label: React.ReactNode;
  /** A unique identifier for the choice */
  id?: string;
  /** Disable choice */
  disabled?: boolean;
  /** Additional text to aide in use */
  helpText?: React.ReactNode;
  /** Indicates that the choice is aria-describedBy the error message */
  describedByError?: boolean;
  /**  Method to render children with a choice */
  renderChildren?: (isSelected: boolean) => React.ReactNode | false;
}

export interface HorseVariantsQueryParams extends BaseQueryParams {
  collection?: "undiscarded" | "discarded" | "all";
  horse_location_id?: number;
  stocked_at_horse_location?: boolean;
  product_type?: string;
  search?: string;
  supplier_id?: number;
  vendor?: string;
  shopify_status?: "draft" | "active";
  without_bundle?: boolean;
  published_shopify_channel_ids?: number[];
  unpublished_shopify_channel_ids?: number[];
  by_cost?: DecimalRangeFilter;
  by_price?: DecimalRangeFilter;
  by_quantity?: DecimalRangeFilter | NumberRangeFilter;
  by_min?: DecimalRangeFilter | NumberRangeFilter;
  by_value?: DecimalRangeFilter;
  by_weight?: DecimalRangeFilter;
  product_tags?: string[];
  by_sort?: SortFilter;
  collection_id?: string;
}

export interface CombineStore {
  id: number;
  shopify_shop_id: number;
  add_store: string;
}

export const HORSE_VARIANTS_TABS = {
  UNDISCARDED: 0,
  ALL: 1,
};

export const HORSE_VARIANTS_COLLECTIONS: Record<number, "undiscarded" | "all"> = {
  [HORSE_VARIANTS_TABS.UNDISCARDED]: "undiscarded",
  [HORSE_VARIANTS_TABS.ALL]: "all",
};
export type DisambiguatedLabel = string | number | DecimalRangeFilter | NumberRangeFilter | string[];

export interface AppliedFilterInterface {
  key: string;
  label: DisambiguatedLabel;
  onRemove: () => void;
}
